import * as THREE from 'three';
import { TerrainFeature } from './TerrainFeature';
import { TerrainType, TERRAIN_GRID, TerrainHeightResult } from './TerrainConfig';

/**
 * Manages all terrain features with spatial grid partitioning for efficient height queries.
 * Similar to HighwayManager's spatial grid system.
 */
export class TerrainManager {
  private features: TerrainFeature[] = [];
  private spatialGrid: Map<string, TerrainFeature[]> = new Map();
  private readonly cellSize: number = TERRAIN_GRID.CELL_SIZE;
  private readonly mapSize: number = TERRAIN_GRID.MAP_SIZE;

  constructor(_scene: THREE.Scene) {
    // Scene reference kept for potential future use (debugging, visualization)
  }

  /**
   * Register a terrain feature with the spatial grid.
   */
  registerFeature(feature: TerrainFeature): void {
    this.features.push(feature);

    // Get bounds and register to all overlapping cells
    const bounds = feature.getBounds();
    const minCellX = Math.floor((bounds.minX + this.mapSize / 2) / this.cellSize);
    const maxCellX = Math.floor((bounds.maxX + this.mapSize / 2) / this.cellSize);
    const minCellZ = Math.floor((bounds.minZ + this.mapSize / 2) / this.cellSize);
    const maxCellZ = Math.floor((bounds.maxZ + this.mapSize / 2) / this.cellSize);

    for (let x = minCellX; x <= maxCellX; x++) {
      for (let z = minCellZ; z <= maxCellZ; z++) {
        const key = this.getCellKey(x, z);
        if (!this.spatialGrid.has(key)) {
          this.spatialGrid.set(key, []);
        }
        this.spatialGrid.get(key)!.push(feature);
      }
    }
  }

  /**
   * Get the cell key for grid coordinates.
   */
  private getCellKey(x: number, z: number): string {
    return `${x},${z}`;
  }

  /**
   * Get cell coordinates from world position.
   */
  private worldToCell(worldPos: THREE.Vector3): { x: number; z: number } {
    return {
      x: Math.floor((worldPos.x + this.mapSize / 2) / this.cellSize),
      z: Math.floor((worldPos.z + this.mapSize / 2) / this.cellSize),
    };
  }

  /**
   * Get height at a world position, checking only relevant terrain features.
   * Returns the maximum height from all overlapping terrain.
   */
  getHeightAtPosition(worldPos: THREE.Vector3): TerrainHeightResult {
    const cell = this.worldToCell(worldPos);
    const key = this.getCellKey(cell.x, cell.z);
    const cellFeatures = this.spatialGrid.get(key);

    if (!cellFeatures || cellFeatures.length === 0) {
      return { height: null, terrainType: null };
    }

    let maxHeight: number | null = null;
    let resultType: TerrainType | null = null;

    for (const feature of cellFeatures) {
      const height = feature.getHeightAtPosition(worldPos);
      if (height !== null && (maxHeight === null || height > maxHeight)) {
        maxHeight = height;
        resultType = feature.getType();
      }
    }

    return { height: maxHeight, terrainType: resultType };
  }

  /**
   * Get all features in a cell (for debugging/visualization).
   */
  getFeaturesInCell(worldPos: THREE.Vector3): TerrainFeature[] {
    const cell = this.worldToCell(worldPos);
    const key = this.getCellKey(cell.x, cell.z);
    return this.spatialGrid.get(key) || [];
  }

  /**
   * Get all registered features.
   */
  getAllFeatures(): TerrainFeature[] {
    return [...this.features];
  }

  /**
   * Get features by type.
   */
  getFeaturesByType(type: TerrainType): TerrainFeature[] {
    return this.features.filter((f) => f.getType() === type);
  }

  /**
   * Get feature count statistics.
   */
  getStats(): { total: number; byType: Record<TerrainType, number> } {
    const byType: Record<TerrainType, number> = {
      launch_hill: 0,
      mountain_peak: 0,
      mountain_ridge: 0,
      hill: 0,
      crater_bowl: 0,
    };

    for (const feature of this.features) {
      byType[feature.getType()]++;
    }

    return { total: this.features.length, byType };
  }

  /**
   * Clean up all terrain features.
   */
  destroy(): void {
    for (const feature of this.features) {
      feature.destroy();
    }
    this.features = [];
    this.spatialGrid.clear();
  }
}
